import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Layout } from 'antd';
import { EditOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import aiChatService, { IConversation, IMessage } from '../../services/aiChat/aiChat.service';
import { useAiChatSocket } from './hooks/useAiChatSocket';
import ConversationSidebar from './components/ConversationSidebar';
import ChatWindow from './components/ChatWindow';
import './aiChat.less';

const { Sider, Content } = Layout;

const PAGE_LIMIT = 20;

const AiChat: React.FC = () => {
  const [siderCollapsed, setSiderCollapsed] = useState(true);
  const [conversations, setConversations] = useState<IConversation[]>([]);
  const [convsPage, setConvsPage] = useState(1);
  const [convsTotal, setConvsTotal] = useState(0);
  const [convsLoading, setConvsLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const {
    isConnected,
    isStreaming,
    partialText,
    toolCalls,
    socketError,
    sendMessage,
    cancelMessage,
    clearSocketError,
    onDone,
    onError,
  } = useAiChatSocket();

  const activeConversationIdRef = useRef<number | null>(null);
  activeConversationIdRef.current = activeConversationId;

  const partialTextRef = useRef('');
  partialTextRef.current = partialText;

  const loadConversations = useCallback(async (page: number, replace: boolean) => {
    setConvsLoading(true);
    try {
      const result = await aiChatService.getConversations(page, PAGE_LIMIT);
      setConvsTotal(result.count);
      setConversations((prev) => (replace ? result.data : [...prev, ...result.data]));
    } catch {
      // 403 = no conversation history access; page still usable for new chats
    } finally {
      setConvsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations(1, true);
  }, [loadConversations]);

  const loadMessages = useCallback(async (conversationId: number, clearFirst = true) => {
    if (clearFirst) {
      setMessages([]);
      setMessagesLoading(true);
    }
    try {
      const result = await aiChatService.getMessages(conversationId);
      setMessages(result.messages);
    } catch {
      // Keep existing messages on failure (e.g. 403 while permissions propagate)
    } finally {
      if (clearFirst) setMessagesLoading(false);
    }
  }, []);

  const handleSelectConversation = useCallback(
    (conv: IConversation) => {
      setActiveConversationId(conv.id);
      loadMessages(conv.id);
    },
    [loadMessages]
  );

  const handleNewChat = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setInputValue('');
  }, []);

  const handleDelete = useCallback(async (id: number) => {
    await aiChatService.deleteConversation(id);
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (activeConversationIdRef.current === id) {
      setActiveConversationId(null);
      setMessages([]);
    }
  }, []);

  const handleLoadMore = useCallback(() => {
    const nextPage = convsPage + 1;
    setConvsPage(nextPage);
    loadConversations(nextPage, false);
  }, [convsPage, loadConversations]);

  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text || isStreaming) return;
    setInputValue('');
    setMessages((prev) => [
      ...prev,
      { id: -Date.now(), role: 'user', content: text, created_date: new Date().toISOString() } as IMessage,
    ]);
    sendMessage(activeConversationIdRef.current, text);
  }, [inputValue, isStreaming, sendMessage]);

  const handleStop = useCallback(() => {
    if (activeConversationIdRef.current != null) {
      cancelMessage(activeConversationIdRef.current);
    }
  }, [cancelMessage]);

  const handleExampleClick = useCallback((prompt: string) => {
    setInputValue(prompt);
  }, []);

  useEffect(() => {
    onDone(async ({ conversation_id, input_tokens, output_tokens }) => {
      const capturedText = partialTextRef.current;
      const wasNew = activeConversationIdRef.current === null;
      setActiveConversationId(conversation_id);
      activeConversationIdRef.current = conversation_id;

      await loadMessages(conversation_id, false);

      setMessages((prev) => {
        if (prev.every((m) => m.id < 0) && capturedText) {
          return [
            ...prev,
            {
              id: -Date.now() - 1,
              role: 'assistant',
              content: capturedText,
              input_tokens_used: input_tokens,
              output_tokens_used: output_tokens,
              created_date: new Date().toISOString(),
            } as IMessage,
          ];
        }
        return prev;
      });

      if (wasNew) {
        await loadConversations(1, true);
        setConvsPage(1);
      } else {
        setConversations((prev) =>
          prev
            .map((c) =>
              c.id === conversation_id
                ? { ...c, updated_date: new Date().toISOString() }
                : c
            )
            .sort(
              (a, b) =>
                new Date(b.updated_date).getTime() - new Date(a.updated_date).getTime()
            )
        );
      }
    });
  }, [onDone, loadMessages, loadConversations]);

  useEffect(() => {
    onError(() => {
      // socketError state is already set in the hook; nothing more to do here
    });
  }, [onError]);

  const hasMore = conversations.length < convsTotal;

  return (
    <div className="ai-chat-page">
      <Layout className="ai-chat-layout">
        {/* Top-left controls: sidebar toggle + New Chat (when sidebar is hidden) */}
        <div className="ai-chat-top-controls">
          <Button
            type="text"
            icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setSiderCollapsed((v) => !v)}
            title={siderCollapsed ? 'Show history' : 'Hide history'}
          />
          {siderCollapsed && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={handleNewChat}
              title="New Chat"
            />
          )}
        </div>

        <Sider
          width={280}
          collapsedWidth={0}
          collapsed={siderCollapsed}
          className="ai-chat-sider"
          theme="light"
          trigger={null}
        >
          <ConversationSidebar
            conversations={conversations}
            activeConversationId={activeConversationId}
            loading={convsLoading}
            hasMore={hasMore}
            onSelect={handleSelectConversation}
            onNewChat={handleNewChat}
            onDelete={handleDelete}
            onLoadMore={handleLoadMore}
          />
        </Sider>

        <Content className="ai-chat-content">
          <ChatWindow
            messages={messages}
            isStreaming={isStreaming}
            partialText={partialText}
            toolCalls={toolCalls}
            socketError={socketError}
            isConnected={isConnected}
            messagesLoading={messagesLoading}
            inputValue={inputValue}
            hasActiveConversation={activeConversationId !== null}
            onInputChange={setInputValue}
            onSend={handleSend}
            onStop={handleStop}
            onClearError={clearSocketError}
            onExampleClick={handleExampleClick}
          />
        </Content>
      </Layout>
    </div>
  );
};

export default AiChat;
